import { AuthService } from '@/services/core/auth'

/**
 * Normalise an API response that may be:
 *  - { results: [...] }  — standard list response
 *  - [...]               — bare array
 *  - { id, ... }         — single object (returned when only one item was posted)
 *
 * The Adnuntius API returns a single object instead of a results array
 * whenever exactly one item is saved via a bulk POST endpoint.
 */
export function toArray<T = Record<string, unknown>>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  if (data !== null && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if ('results' in obj && Array.isArray(obj.results)) return obj.results as T[]
    return [obj] as T[]
  }
  return []
}

let refreshPromise: Promise<void> | null = null

const LOCAL_API_HOSTS = new Set(['localhost', '127.0.0.1'])

function requestUrl(input: RequestInfo | URL): URL {
  const value = input instanceof Request ? input.url : input.toString()
  return new URL(value, window.location.origin)
}

function withAuth(input: RequestInfo | URL, init?: RequestInit) {
  const token = AuthService.getAccessToken()
  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined)
  )
  const url = requestUrl(input)
  const isLocalApi = LOCAL_API_HOSTS.has(url.hostname)
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()

  if (token) {
    if (isLocalApi || input instanceof Request) {
      headers.set('Authorization', `Bearer ${token}`)
    } else {
      // Match UII's deployed request path: the API accepts auth_token and redacts
      // it from request logs. Avoiding Authorization keeps reads CORS-simple.
      url.searchParams.set('auth_token', token)
      headers.delete('Authorization')
    }
  }

  if (!isLocalApi && (method === 'GET' || method === 'HEAD')) {
    // A body-less read has no content type. Several older services still add
    // application/json, which turns an otherwise simple request into a preflight.
    headers.delete('Content-Type')
  }

  const authenticatedInput =
    input instanceof Request ? input : input instanceof URL ? url : url.toString()

  return { input: authenticatedInput, init: { ...init, headers } }
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const request = withAuth(input, init)
  const res = await fetch(request.input, request.init)

  if (res.status !== 401) return res

  // Avoid concurrent refresh attempts
  if (!refreshPromise) {
    refreshPromise = AuthService.refresh()
      .then(() => undefined)
      .catch(() => {
        AuthService.clearToken()
        window.location.href = '/'
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  await refreshPromise

  // If refresh failed the page is navigating away; return original response
  if (!AuthService.isAuthenticated()) return res

  const retry = withAuth(input, init)
  return fetch(retry.input, retry.init)
}
